import { useState } from "react";
import { toast } from "react-toastify";

import {
  TransportResourceImportVendorError,
  TransportResourceImportVendorRes,
  useTransportResourceServiceV1ImportVendorMutation,
} from "_services/modules/transport-resource/vendor";

import { errorHandler } from "_services/errorHandler";
import { env } from "config/env";

export const useVendorImport = (
  onSuccess?: (response?: TransportResourceImportVendorRes) => void,
) => {
  const [isOpen, setIsOpen] = useState(false);

  const [selectedFile, setSelectedFile] =
    useState<File | null>(null);

  const [validationErrors, setValidationErrors] =
    useState<TransportResourceImportVendorError[]>([]);

  const [
    importVendor,
    { isLoading },
  ] = useTransportResourceServiceV1ImportVendorMutation();



  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);

    setSelectedFile(null);

    setValidationErrors([]);
  };

  const onSelectFile = (
    file: File | null,
  ) => {
    if (!file) {
      return;
    }

    const fileName = file.name || "";
    const fileExtension = fileName.split(".").pop()?.toLowerCase()?.trim() || "";
    const allowedExtensions = ["xlsx", "xls", "csv"];

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Only .xlsx and .xls files are allowed");
      return;
    }

    const maxSize =
      10 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error(
        "Maximum file size is 10 MB",
      );

      return;
    }

    setSelectedFile(file);

    setValidationErrors([]);
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const submitImport = async () => {
    try {
      if (!selectedFile) {
        toast.error(
          "Please select a file",
        );

        return;
      }

      const formData = new FormData();

      formData.append(
        "file",
        selectedFile,
      );
      const response = await importVendor({
        // as any is used because OpenAPI schema expects strict ApiHttpBody type but file upload requires FormData
        apiHttpBody: formData as any,
      }).unwrap();
      if (response.success === false && response.errors) {
        setValidationErrors(response.errors);

        toast.error("Import validation failed");

        return;
      }

      closeModal();

      onSuccess?.(response);
    } catch (error: any) { // Catch block error defaults to any
      const backendErrors =
        error?.data?.errors;

      if (
        Array.isArray(
          backendErrors,
        )
      ) {
        setValidationErrors(
          backendErrors,
        );
      }

      errorHandler(error);
    }
  };

  const downloadTemplate = () => {
    if (env.VENDOR_IMPORT_TEMPLATE_URL) {
      window.open(env.VENDOR_IMPORT_TEMPLATE_URL, "_blank");
    } else {
      toast.error("Template URL is not configured");
    }
  };

  return {
    isOpen,
    selectedFile,
    validationErrors,
    isLoading,

    openModal,
    closeModal,

    onSelectFile,
    removeFile,

    submitImport,
    downloadTemplate,
  };
};